package dev.lingvo.site.pages.app.projects.slug

import androidx.compose.runtime.Composable
import androidx.compose.runtime.key
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LSelect
import dev.lingvo.site.ds.LSelectOption
import dev.lingvo.site.ds.LTextField
import dev.lingvo.site.ds.Space
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.dom.Div

private const val PROJECT_PATH_EXAMPLE = "app/src/main/res/values/strings.xml"

/** A local upload is content; the project path and optional UUID are its explicit destination. */
@Composable
internal fun ImportDestinationControls(flow: ImportPreviewState, onRefresh: () -> Unit) {
    val selected = flow.destination
    val files = flow.destinationFiles
    val failure = flow.destinationFilesFailure
    val unavailable = selected != null && files.none { it.id == selected.id }
    val width = Modifier.fillMaxWidth().maxWidth(26.cssRem).styleModifier { property("min-width", "0") }
    Column(Modifier.fillMaxWidth().gap(Space.SM)) {
        LMuted(t("app.projects.slug.import_destination_controls.choose_project_destination",
            "Choose the project file to update, or enter its full project path. The upload name is separate."))
        val options = listOf(LSelectOption("", t("app.projects.slug.import_destination_controls.use_project_path", "Use a project file path"))) +
            files.sortedBy { it.path }.map { LSelectOption(it.id, it.path) } +
            if (unavailable) listOf(LSelectOption(selected!!.id,
                tf("app.projects.slug.import_destination_controls.unavailable_path", "{path} (unavailable)", "path" to selected.path), disabled = true)) else emptyList()
        // Rebuild changed option sets so the browser retains a selected, disabled missing file.
        // Available-file choices retain this key, keeping focus during ordinary selection.
        key(files.map { Triple(it.id, it.path, it.format) }, unavailable) {
            LSelect(
                value = selected?.id.orEmpty(),
                options = options,
                onValueChange = { raw ->
                    if (raw.isEmpty()) flow.changeDestination(null)
                    else files.singleOrNull { it.id == raw }?.let(flow::changeDestination)
                },
                modifier = width,
                label = t("app.projects.slug.import_destination_controls.destination_file", "Destination file"),
                enabled = !flow.destinationFilesLoading,
            )
        }
        if (selected == null) {
            LTextField(flow.path, flow::changePath, width,
                label = t("app.projects.slug.import_destination_controls.project_file_path", "Project file path"),
                placeholder = PROJECT_PATH_EXAMPLE)
        } else {
            Div(Modifier.fillMaxWidth().styleModifier { property("overflow-wrap", "anywhere") }.toAttrs()) {
                LMuted(tf("app.projects.slug.import_destination_controls.selected_project_path", "Project file: {path}", "path" to selected.path))
            }
        }
        LMuted(t("app.projects.slug.import_destination_controls.source_path_for_languages",
            "Use the source file’s project path for each language. Source imports can create files; translation imports need an existing file."))
        LMuted(t("app.projects.slug.import_destination_controls.parser_is_upload_format",
            "Import format describes the uploaded file. Changing it does not rename the project file or change its export format."))
        when {
            flow.destinationFilesLoading -> LMuted(t("app.projects.slug.import_destination_controls.reading_project_files", "Reading the project file list…"))
            failure != null -> LAlert(
                title = if (failure.isRefusal || failure.isMissing) {
                    t("app.projects.slug.import_destination_controls.project_files_unavailable", "The project files are unavailable")
                } else t("app.projects.slug.import_destination_controls.couldnt_refresh_files", "Couldn’t refresh the project file list"),
                description = failure.readable(), variant = LAlertVariant.WARNING, live = true,
            )
            unavailable -> LAlert(
                title = t("app.projects.slug.import_destination_controls.selected_file_missing", "The selected project file is no longer available"),
                description = t("app.projects.slug.import_destination_controls.choose_or_refresh",
                    "Choose another destination or refresh the file list. Your upload is retained; no replacement file is selected automatically."),
                variant = LAlertVariant.WARNING, live = true,
            )
        }
        if (failure?.let { it.isRefusal || it.isMissing } != true) {
            LButton(
                if (failure == null) t("app.projects.slug.import_destination_controls.refresh_file_list", "Refresh file list")
                else t("app.projects.slug.import_destination_controls.retry_file_list", "Retry file list"),
                onRefresh, variant = LButtonVariant.SECONDARY, size = LButtonSize.SM,
                enabled = !flow.destinationFilesLoading && !flow.busy,
            )
        }
    }
}
